import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { CartProvider } from './hooks/useCart';
import Header from './components/Header';
import Footer from './components/Footer';
import JokesTicker from './components/JokesTicker';
import FallingAnimation from './components/FallingAnimation';
import ContentWarningModal from './components/ContentWarningModal';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductEdit from './pages/admin/AdminProductEdit';
import AdminImageCleanup from './pages/admin/AdminImageCleanup';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminUsers from './pages/admin/AdminUsers';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminPages from './pages/admin/AdminPages';
import AdminShippingSettings from './pages/admin/AdminShippingSettings';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLogs from './pages/admin/AdminLogs';
import AccountDashboard from './pages/account/AccountDashboard';
import OrderHistory from './pages/account/OrderHistory';
import OrderDetails from './pages/account/OrderDetails';
import AccountSettings from './pages/account/AccountSettings';
import Wishlist from './pages/account/Wishlist';
import Addresses from './pages/account/Addresses';
import './App.css';

function App() {
  return (
    <CartProvider>
      <Router>
        <AppContent />
      </Router>
    </CartProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [seasonalAnimation, setSeasonalAnimation] = useState({
    enabled: false,
    type: 'snow',
    count: 25,
    speed: 'medium'
  });

  useEffect(() => {
    // Fetch seasonal animation settings
    const fetchSeasonalAnimationSettings = async () => {
      try {
        const response = await axios.get('/api/settings/seasonal-animation');
        setSeasonalAnimation({
          enabled: response.data.enabled,
          type: response.data.type,
          count: response.data.count,
          speed: response.data.speed
        });
      } catch (error) {
        // Fail silently - animation just won't show
        console.error('Failed to fetch seasonal animation settings:', error);
      }
    };

    if (!isAdminRoute) {
      fetchSeasonalAnimationSettings();
    }
  }, [isAdminRoute]);

  return (
    <div className="App">
      <ContentWarningModal />
      {!isAdminRoute && <JokesTicker />}
      {!isAdminRoute && <Header />}
      {!isAdminRoute && (
        <FallingAnimation
          type={seasonalAnimation.type}
          enabled={seasonalAnimation.enabled}
          count={seasonalAnimation.count}
          speed={seasonalAnimation.speed}
        />
      )}
      <main className={isAdminRoute ? "admin-main-content" : "main-content"}>
        <Routes>
              {/* Public routes */}
              <Route path="/" element={<Navigate to="/shop" replace />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Admin routes */}
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/products" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminProducts />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/products/:id/edit" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminProductEdit />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/image-cleanup" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminImageCleanup />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminOrders />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/orders/:id" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminOrderDetail />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminUsers />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/transactions" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminTransactions />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminPages />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/shipping-settings" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminShippingSettings />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminSettings />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/logs" element={
                <ProtectedRoute requireRole="admin">
                  <AdminLayout>
                    <AdminLogs />
                  </AdminLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={<LoginPage />} />

              {/* Customer account routes */}
              <Route path="/account" element={
                <ProtectedRoute requireRole="customer">
                  <AccountDashboard />
                </ProtectedRoute>
              } />
              <Route path="/account/orders" element={
                <ProtectedRoute requireRole="customer">
                  <OrderHistory />
                </ProtectedRoute>
              } />
              <Route path="/account/orders/:id" element={
                <ProtectedRoute requireRole="customer">
                  <OrderDetails />
                </ProtectedRoute>
              } />
              <Route path="/account/settings" element={
                <ProtectedRoute requireRole="customer">
                  <AccountSettings />
                </ProtectedRoute>
              } />
              <Route path="/account/wishlist" element={
                <ProtectedRoute requireRole="customer">
                  <Wishlist />
                </ProtectedRoute>
              } />
              <Route path="/account/addresses" element={
                <ProtectedRoute requireRole="customer">
                  <Addresses />
                </ProtectedRoute>
              } />
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
}

export default App;
